import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setMenu } from "../store/reducers/utilityReducer";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

const HelpAndSupportPage = () => {
  const navigate = useNavigate();
  const { bussinessUuid } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  
  const [faqs, setFaqs] = useState([
    {
      questionKey: "SUPPORT.HOW_DO_I_SET_UP_MY_ACCOUNT",
      answerKey: "SUPPORT.TO_CREATE_AN_ACCOUNT_CLICK_ON_THE_BUTTON",
      isOpen: false,
    },
    {
      questionKey: "SUPPORT.HOW_CAN_I_GET_STARTED_WITH_DUKANBUDDY",
      answerKey: "SUPPORT.YOU_CAN_GET_STARTED_THE_ONBOARDING_PROCESS",
      isOpen: false,
    },
    {
      questionKey: "SUPPORT.HOW_DO_I_USE_THE_MARKET_INSIGHT",
      answerKey: "SUPPORT.MARKET_INSIGHT_OFFERS",
      isOpen: false,
    },
    {
      questionKey: "SUPPORT.HOW_DO_I_USE_FEATURE",
      answerKey: "SUPPORT.TO_BEGIN_NAVIGATE",
      isOpen: false,
    },
  ]);

  useEffect(() => {
    dispatch(
      setMenu(<span className="text-lg font-semibold">{t("MENU.HELP_AND_SUPPORT")}</span>)
    );
  }, [dispatch, t]);

  const toggleAccordion = (index) => {
    setFaqs((prevFaqs) =>
      prevFaqs.map((faq, i) =>
        i === index ? { ...faq, isOpen: !faq.isOpen } : faq
      )
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section - Wider */}
        <div className="mb-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 mb-6">
            <svg 
              className="w-8 h-8 text-white" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
          </div>
       
          <p className="text-2xl text-gray-700  font-bold dark:text-gray-300 max-w-3xl mx-auto">
            {t("Common Questions")}
          </p>
        </div>

        {/* Create Ticket Button - Top Right */}
        <div className="mb-12 flex justify-end">
          <button
            onClick={() => navigate(`/${bussinessUuid}/dashboard/create-ticket`)}
            className="group relative inline-flex items-center gap-3 px-8 py-3.5 bg-gradient-to-r from-blue-600 to-blue-700 
              text-white rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 
              transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 
              focus:outline-none focus:ring-3 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
          >
            <svg 
              className="w-6 h-6" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 6v6m0 0v6m0-6h6m-6 0H6" 
              />
            </svg>
            <span className="text-base">{t("SUPPORT.CREATE_TICKET")}</span>
            {/* <span className="absolute -bottom-1 left-1/2 w-0 h-1 bg-white/80 group-hover:w-4/5 group-hover:left-1/10 transition-all duration-300 rounded-full"></span> */}
          </button>
        </div>

        {/* FAQ Section - Full Width */}
        <div className="space-y-5">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="group relative overflow-hidden"
            >
              {/* Background glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 dark:from-blue-900/10 dark:to-indigo-900/10 
                opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
              
              <div
                className="relative border border-gray-200 dark:border-gray-700 rounded-xl p-6 cursor-pointer 
                  bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm 
                  transition-all duration-300 hover:border-blue-400 dark:hover:border-blue-600
                  hover:shadow-xl hover:shadow-blue-100/30 dark:hover:shadow-blue-900/10
                  w-full"
                onClick={() => toggleAccordion(index)}
              >
                <div className="flex justify-between items-start gap-6">
                  {/* Question Section - Full Width */}
                  <div className="flex-1">
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div className={`flex-shrink-0 mt-1 p-3 rounded-xl 
                        ${faq.isOpen 
                          ? 'bg-gradient-to-r from-blue-100 to-blue-200 dark:from-blue-900/40 dark:to-blue-800/40 text-blue-600 dark:text-blue-400' 
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                        } transition-all duration-300`}
                      >
                        <svg 
                          className="w-6 h-6" 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          {faq.isOpen ? (
                            <path 
                              strokeLinecap="round" 
                              strokeLinejoin="round" 
                              strokeWidth={2} 
                              d="M5 15l7-7 7 7" 
                            />
                          ) : (
                            <path 
                              strokeLinecap="round" 
                              strokeLinejoin="round" 
                              strokeWidth={2} 
                              d="M19 9l-7 7-7-7" 
                            />
                          )}
                        </svg>
                      </div>
                      
                      {/* Question Content */}
                      <div className="flex-1">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 pr-8">
                          {t(faq.questionKey)}
                        </h2>
                        
                        {faq.isOpen && (
                          <div className="mt-5 ml-2">
                            <div className="flex gap-4">
                              <div className="flex-shrink-0 mt-2">
                                <div className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-blue-600"></div>
                              </div>
                              <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-base pr-8">
                                {t(faq.answerKey)}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Chevron - Right Side */}
                  <div className="flex-shrink-0">
                    <svg
                      className={`w-7 h-7 text-gray-500 dark:text-gray-400 transition-all duration-300 transform
                        ${faq.isOpen ? "rotate-180 text-blue-600 dark:text-blue-400 scale-110" : ""}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Need More Help Section - Full Width */}
        <div className="mt-16 p-8 rounded-2xl bg-gradient-to-r from-blue-50/90 to-indigo-50/90 
          dark:from-blue-900/30 dark:to-indigo-900/30 border border-blue-200 dark:border-blue-800/40
          backdrop-blur-sm">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="flex-shrink-0 p-4 rounded-xl bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900/40 dark:to-indigo-900/40">
                <svg 
                  className="w-7 h-7 text-blue-600 dark:text-blue-400" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" 
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  {t("Still Have Questions?")}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 text-base">
                  {t("SUPPORT.OUR_TEAM_WILL_REACH_OUT_TO_YOU_SHORTLY")}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate(`/${bussinessUuid}/dashboard/create-ticket`)}
              className="px-6 py-3 text-base font-semibold text-blue-600 dark:text-blue-400 
                hover:text-blue-700 dark:hover:text-blue-300 bg-white dark:bg-gray-800 
                hover:bg-blue-50 dark:hover:bg-blue-900/30 
                rounded-xl transition-all duration-300 hover:shadow-md border border-blue-200 dark:border-blue-800/40
                min-w-[180px] text-center"
            >
              {t("Contact Us")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpAndSupportPage;